import { useState, useEffect } from "react";
import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useT } from "@/lib/translation";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ContactUsForm } from "./ContactUsForm";
import { getMe } from "@/api/client";

type ContactUsModalProps = {
  children?: ReactNode;
  /** Public = show anti-spam UI; Auth = minimal UI (server still protects) */
  mode?: "public" | "auth";
};

export function ContactUsModal({
  children,
  mode = "public",
}: ContactUsModalProps) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [openedAt, setOpenedAt] = useState<number | null>(null);

  const { data: me } = useQuery({
    queryKey: ["me"],
    queryFn: getMe,
    enabled: mode === "auth",
  });

  useEffect(() => {
    if (open) {
      setOpenedAt(Date.now());
    } else {
      setOpenedAt(null);
    }
  }, [open]);

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {children ?? (
          <Button variant="ghost" className="w-full justify-start">
            {t("contact:modal.triggerLabel")}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("contact:modal.title")}</DialogTitle>
        </DialogHeader>
        {open && openedAt && (
          <ContactUsForm
            mode={mode}
            openedAt={openedAt}
            onSuccess={() => setOpen(false)}
            defaultEmail={me?.username}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
